import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { RouterState } from 'connected-react-router'
import moment from 'moment'
import { type SelfDescribingEvent, type CommonEventProperties, type SelfDescribingJson, setUserId } from '@snowplow/browser-tracker'

import { AppStore } from '../../store'
import config from 'constants/config'
import { USER_CONTEXT_SCHEMA } from 'constants/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { SearchEventData } from 'contexts/GlobalSearch/GlobalSearchTracking'
import {
  buildCartContext,
  buildInsuranceUpsellContext,
  buildSearchContext,
  cartEventsMap,
  createOfferItemContexts,
  createTourOfferItemContexts,
  experienceOfferEventsMap,
  frontPageEventsMap,
  getExperienceContext,
  getOfferContext,
  mapToSnowplowFilters,
  offerEventsMap,
  getPageOfferCategory,
} from './helpers'
import type {
  CartExtras,
  CartTrackingEvent,
  ExperienceEventDefinitionKey,
  ExperienceEventEntity,
  ExperienceOfferEventContext,
  FrontPageEvent,
  SaveToTripBookingDetails,
} from './helpers'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { reportError } from 'services/errorReportingService'
import { GAImpressionFieldObject, GAProductFieldObject, SnowplowImpressionData } from 'analytics/eventDefinitions'
import { OptimizelyEvent } from 'analytics/optimizely/events'
import { getPageCategoryAndType } from './analyticsPageMapping'
import { OfferContextEvent } from './context'

export type SnowplowEvent = SelfDescribingEvent & CommonEventProperties

let _store: AppStore | undefined
let _pageContext: SelfDescribingJson[] = []

export function registerCartTrackingStore(store: AppStore) {
  _store = store
}

/**
 * Event definitions
 */
export function impressionEvent(
  obj: GAImpressionFieldObject | GAProductFieldObject,
  listId: string,
  listName: string,
  currency: string,
  schema: string,
  lereVersion?: string,
): SnowplowEvent {
  const data: SnowplowImpressionData = {
    id: obj.id,
    name: obj.name,
    productType: obj.category,
    position: obj.position,
    currency,
    price: obj.price,
    listId,
    listName,
    listSource: lereVersion && `lere-${lereVersion}`,
  }
  return {
    event: {
      schema,
      data,
    },
  }
}

export function agentInsuranceUpselEvent(
  notSuitable: string,
  yes: string,
  no: string,
  whyNotSuitable?: string,
  isCovermore?: string,
  isOffered?: string,
  declineReason?: string,
): SnowplowEvent {
  return {
    context: buildInsuranceUpsellContext(
      notSuitable,
      yes,
      no,
      whyNotSuitable,
      isCovermore,
      isOffered,
      declineReason,
    ),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'insurance_upsell_reminder',
      type: 'interaction',
      action: 'submit',
      category: 'checkout',
    }),
  }
}
export function cartEvent(eventType: CartTrackingEvent, extras: CartExtras = {}): SnowplowEvent | undefined {
  try {
    const { paymentType, errorReason } = extras
    if (!_store) {
      throw new Error('No redux registered for tracking cart snowplow events')
    }
    const cartState: App.CartState = _store.getState().cart
    const eventData = cartEventsMap[eventType]
    return {
      context: [buildCartContext(cartState, paymentType, errorReason)],
      event: comluxgroup.createClientEvent_1_0_0({
        ...eventData,
      }),
    }
  } catch (e) {
    reportError(e)
  }
}

export function experienceEvent(
  eventDefinitionKey: ExperienceEventDefinitionKey,
  geo: App.GeoState, eventsContexts?: Array<ExperienceEventEntity>,
): SnowplowEvent {
  const event = experienceOfferEventsMap[eventDefinitionKey]
  const eventsContextsWithGeo = eventsContexts?.map<ExperienceOfferEventContext>(eventContext => ({
    ...eventContext,
    data: {
      ...eventContext.data,
      geo,
    },
  })) ?? []
  return {
    context: eventsContextsWithGeo?.length ? getExperienceContext(eventsContextsWithGeo) : [],
    event: comluxgroup.createClientEvent_1_0_0({
      ...event,
      category: 'experiences_offer',
    }),
  }
}

export function clientEvent(
  subject: string,
  action: string,
  category: string,
  type: 'interaction' | 'nonInteraction' | 'operational',
): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject,
      action,
      type,
      category,
    }),
  }
}

export function uspAboutUsEvent(
  subject: string,
): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      subject,
      action: 'clicked',
      category: 'usp_about_us',
      type: 'interaction',
    }),
  }
}

export type AuthState = 'started' | 'succeeded' | 'failed'

export function logOperational(subject: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logRegistration(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_registration`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function logAuth(provider: App.AuthSource, data: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action: `${data}_succeeded`,
      subject: `${provider}_auth`,
      category: 'logging',
      type: 'operational',
    }),
  }
}

export function offerEvent(eventType: OfferContextEvent, offerPageContext: OfferPageState | undefined): SnowplowEvent | undefined {
  try {
    const ctx = getOfferContext(offerPageContext)
    if (ctx) {
      const event = offerEventsMap[eventType]

      return {
        context: [
          // ctx Re-enable after [REC-780]
        ],
        event: comluxgroup.createClientEvent_1_0_0({
          ...event,
          category: 'offer_page',
        }),
      }
    }
  } catch (e) {
    reportError(e)
  }
}

export function offerPageHotLeadEvent(
  offer: App.Offer | App.BedbankOffer | Tours.V2Offer | App.CruiseOffer,
  lead: App.HotLeadOffer,
  geo: App.GeoState,
): SnowplowEvent | undefined {
  try {
    const duration = lead.checkOut && lead.checkIn ? moment(lead.checkOut).diff(lead.checkIn, 'days') : undefined
    const contextItems = lead.occupancy.map(room => comluxgroup.createItem_1_0_0({
      brand: config.BRAND,
      currency: geo.currentCurrency,
      region: geo.currentRegionCode,
      offerId: offer.id,
      offerType: offer.type,
      travelStart: lead.checkIn,
      travelEnd: lead.checkOut,
      year: lead.year?.toString(),
      month: lead.month?.toString(),
      variant: lead.variant,
      duration,
      childrenAges: room.childrenAge,
      numberOfAdults: room.adults,
      numberOfChildren: room.children,
      numberOfInfants: room.infants,
    }))

    return {
      context: contextItems,
      event: comluxgroup.createClientEvent_1_0_0({
        subject: 'hot_lead',
        type: 'interaction',
        action: 'sent',
        category: 'offer_page',
      }),
    }
  } catch (e) {
    reportError(e)
  }
}

export function frontPageClientEvent(eventType: FrontPageEvent, existingFilters?: App.OfferListFilters) {
  const uiSearchContext = comluxgroup.createSearch_1_0_0(buildSearchContext({
    searchBar: 'secondaryFilter',
    searchType: 'offerListFilters',
    searchParams: {
      filters: existingFilters ? mapToSnowplowFilters(existingFilters) : undefined,
    },
  }))
  return {
    context: [uiSearchContext],
    event: comluxgroup.createClientEvent_1_0_0(frontPageEventsMap[eventType]),
  }
}

export function searchEvent(subject: string, category: string, data: SearchEventData) {
  const { searchItem, checkIn, checkOut, vertical, isFlexibleDates, occupancies, filters } = data
  const baseContext = buildSearchContext()

  const formattedOccupancies = (occupancies ?? []).map(
    occupants => ({ numberOfChildren: (occupants?.infants ?? 0) + (occupants?.children ?? 0), numberOfAdults: occupants.adults }))

  return {
    context: [comluxgroup.createSearch_1_0_0({
      ...baseContext,
      searchType: searchItem?.searchType ?? '',
      verticals: [vertical],
      searchParams: {
        ...baseContext.searchParams,
        checkIn: checkIn ?? null,
        checkOut: checkOut ?? null,
        occupancies: formattedOccupancies,
        isFlexibleDates: Boolean(isFlexibleDates),
        searchText: searchItem?.format?.mainText ?? null,
        filters: filters ? mapToSnowplowFilters(filters) : null,
      },
    })],
    event: comluxgroup.createClientEvent_1_0_0({
      action: 'clicked',
      subject,
      type: 'interaction',
      category,
    }),
  }
}

/**
 *
 * Refresh the analytics base context for page category and type
 * Has to be async in the case where the offer hasn't loaded yet and the offer needs to be loaded
 * This has to happen as e.g. ultralux could be many verticals that can't reliably be determined from the URL or ID alone
 *
 * @returns {Promise<void>}
 */
export async function refreshAnalyticsBaseContext() : Promise<void> {
  if (!_store) {
    return
  }

  const routerState: RouterState = _store.getState().router

  // See if the page is part of the mapped analytics pages
  const pageData = getPageCategoryAndType(routerState.location.pathname)

  // Otherwise, see if it is an offer page
  const pageCategory = await getPageOfferCategory(_store.getState())
  if (!pageData.category && !pageData.type && pageCategory) {
    pageData.category = pageCategory
    pageData.type = 'offer'
  }

  _pageContext = [
    comluxgroup.createPage_1_0_0({
      type: pageData.type,
      category: pageData.category,
    }),
  ]
}

export function baseContext() : SelfDescribingJson[] {
  if (!_store) {
    return []
  }

  const authState: App.AuthState = _store.getState().auth
  const geoState: App.GeoState = _store.getState().geo

  const context = [
    ..._pageContext,
    comluxgroup.createBrand_1_0_0({
      brand: config.BRAND,
    }),
    comluxgroup.createRegion_1_0_0({
      region: geoState.currentRegionCode,
    }),
  ]
  if (authState.account.memberId && authState.account.email) {
    setUserId(authState.account.memberId)
    context.push({
      schema: USER_CONTEXT_SCHEMA,
      data: {
        id: authState.account.memberId,
        email: authState.account.email,
      },
    })
  }
  return context
}

export function signupSuccessEvent(method: App.AuthSource) {
  return {
    event: comluxgroup.createSignupSuccess_1_0_0({
      method,
    }),
  }
}

export function addToTripDismissed(
  offer: App.AnyOffer,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  bookingDetails: SaveToTripBookingDetails = {},
): SnowplowEvent {
  return {
    context: createOfferItemContexts(offer, regionCode, currency, bookingDetails),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'dismissed_save_to_trip',
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function saveToTrip(
  offer: App.AnyOffer,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  bookingDetails: SaveToTripBookingDetails = {},
): SnowplowEvent & OptimizelyEvent {
  return {
    eventName: 'save-to-trip',
    type: 'event',
    context: createOfferItemContexts(offer, regionCode, currency, bookingDetails),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: 'pressed_save_to_trip',
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function selectPackageUpgradeOptionToCart(
  offer: Tours.V2Offer,
  option: string,
  triggerLocation: AnalyticsPage,
  regionCode: string,
  currency: string,
  tourItems: App.Checkout.TourV2Item[],
): SnowplowEvent {
  return {
    context: createTourOfferItemContexts(offer, regionCode, currency, tourItems),
    event: comluxgroup.createClientEvent_1_0_0({
      subject: `tour_upgrade_${option.toLowerCase()}`,
      type: 'interaction',
      action: 'pressed',
      category: triggerLocation,
    }),
  }
}

export function tourTrendingDestination(country: string, action: string): SnowplowEvent {
  return {
    context: [],
    event: comluxgroup.createClientEvent_1_0_0({
      action,
      subject: `tour_trending_destinations_${country}`,
      category: 'vertical_page',
      type: 'interaction',
    }),
  }
}
